import React, { useState } from 'react';
import {
  Clock, Trash2, Database, AlertTriangle, Settings, Play,
  RefreshCw, Shield, Info, Check, X, Loader2
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormat } from '../../i18n/format.js';
import { apiRequest } from '../../api/core.js';

function DataRetentionPanel({ onMessage }) {
  const { t, fmt } = useFormat();
  const queryClient = useQueryClient();
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [purging, setPurging] = useState(false);

  // Fetch retention policies (tenant-isolated via middleware)
  const { data: policiesData, isLoading } = useQuery({
    queryKey: ['retention_policies'],
    queryFn: () => apiRequest('/gdpr-compliance/retention-policies')
  });

  // Fetch purge statistics
  const { data: purgeStats } = useQuery({
    queryKey: ['purge_stats'],
    queryFn: () => apiRequest('/gdpr-compliance/purge/stats'),
    refetchInterval: 60000 // Refresh every minute
  });

  // Fetch purge history (tenant-isolated via middleware)
  const { data: purgeHistoryData } = useQuery({
    queryKey: ['purge_history'],
    queryFn: () => apiRequest('/gdpr-compliance/purge/history')
  });

  // Update retention policy (ownership verified by middleware)
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => apiRequest(`/gdpr-compliance/retention-policies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['retention_policies']);
      setEditingPolicy(null);
      onMessage?.({ type: 'success', text: t('compliance.retention.toast.policyUpdated') });
    },
    onError: (err) => {
      onMessage?.({ type: 'error', text: t('compliance.retention.errors.update', { message: err.message }) });
    }
  });

  // Execute purge mutation
  const purgeMutation = useMutation({
    mutationFn: () => apiRequest('/gdpr-compliance/purge/execute', {
      method: 'POST',
      body: JSON.stringify({})
    }),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['purge_stats']);
      queryClient.invalidateQueries(['purge_history']);
      setShowPurgeConfirm(false);
      setPurging(false);
      const totalDeleted = data.data?.total_deleted || 0;
      onMessage?.({ type: 'success', text: t('compliance.retention.toast.purgeDone', { count: totalDeleted }) });
    },
    onError: (err) => {
      setPurging(false);
      onMessage?.({ type: 'error', text: t('compliance.retention.errors.purge', { message: err.message }) });
    }
  });

  const handlePurge = () => {
    setPurging(true);
    purgeMutation.mutate();
  };

  const policies = policiesData?.data || [];
  const purgeHistory = purgeHistoryData?.data || [];
  const stats = purgeStats?.data?.stats || [];
  const totalToPurge = purgeStats?.data?.total_to_purge || 0;

  const getCategoryIcon = (category) => {
    const icons = {
      'location_history': '📍',
      'telematics_alerts': '⚡',
      'performance_scores': '📊',
      'audit_logs': '📋',
      'consent_records': '✅',
      'claim_records': '📄',
      'maintenance_records': '🔧',
      'trip_history': '🛣️'
    };
    return icons[category] || '📁';
  };

  const formatDays = (days) => (
    days >= 365
      ? t('compliance.retention.duration.years', { count: Math.floor(days / 365) })
      : t('compliance.retention.duration.days', { count: days })
  );

  const formatDate = (dateStr) => (dateStr ? fmt.dateTime(dateStr) : t('common.states.notAvailable'));

  // Le code de catégorie vient de la base : libellé traduit si connu, code lisible sinon.
  const categoryLabel = (category) => t(`compliance.retention.categories.${category}`, {
    defaultValue: String(category || '').replace(/_/g, ' '),
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t('compliance.retention.title')}</h3>
          <p className="text-sm text-gray-500">
            {t('compliance.retention.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowPurgeConfirm(true)}
          disabled={totalToPurge === 0}
          className={`px-4 py-2 rounded-lg flex items-center ${
            totalToPurge > 0
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <Play className="w-4 h-4 mr-2" />
          {t('compliance.retention.purgeButton')} {totalToPurge > 0 && `(${totalToPurge})`}
        </button>
      </div>

      {/* GDPR Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-800">{t('compliance.retention.info.title')}</h4>
            <p className="text-sm text-blue-700 mt-1">
              {t('compliance.retention.info.body')}
            </p>
          </div>
        </div>
      </div>

      {/* Policies Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <h4 className="font-semibold">{t('compliance.retention.policies.title')}</h4>
        </div>
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">{t('common.states.loading')}</div>
        ) : policies.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {t('compliance.retention.policies.empty')}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('compliance.retention.table.category')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('compliance.retention.table.description')}</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">{t('compliance.retention.table.duration')}</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">{t('compliance.retention.table.toPurge')}</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">{t('compliance.retention.table.autoDelete')}</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">{t('compliance.retention.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {policies.map(policy => {
                const stat = stats.find(s => s.category === policy.data_category);
                return (
                  <tr key={policy.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <span className="text-xl mr-2">{getCategoryIcon(policy.data_category)}</span>
                        <span className="font-medium capitalize">
                          {categoryLabel(policy.data_category)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{policy.description}</td>
                    <td className="px-4 py-3 text-center">
                      {editingPolicy === policy.id ? (
                        <input
                          type="number"
                          defaultValue={policy.retention_days}
                          className="w-20 border rounded px-2 py-1 text-center"
                          onBlur={(e) => {
                            updateMutation.mutate({
                              id: policy.id,
                              data: { retention_days: parseInt(e.target.value) }
                            });
                          }}
                        />
                      ) : (
                        <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                          {formatDays(policy.retention_days)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {stat?.records_to_purge > 0 ? (
                        <span className="inline-flex items-center text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {stat.records_to_purge}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {policy.auto_delete_enabled ? (
                        <span className="inline-flex items-center text-green-600">
                          <Check className="w-4 h-4 mr-1" /> {t('compliance.retention.autoDelete.active')}
                        </span>
                      ) : (
                        <span className="text-gray-400">{t('compliance.retention.autoDelete.inactive')}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setEditingPolicy(editingPolicy === policy.id ? null : policy.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Data Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          icon={Database}
          title={t('compliance.retention.stats.categories.title')}
          value={t('compliance.retention.stats.categories.value', { count: policies.length })}
          description={t('compliance.retention.stats.categories.description')}
          color="blue"
        />
        <StatCard
          icon={Clock}
          title={t('compliance.retention.stats.toPurge.title')}
          value={t('compliance.retention.stats.toPurge.value', { count: totalToPurge })}
          description={t('compliance.retention.stats.toPurge.description')}
          color="amber"
        />
        <StatCard
          icon={Trash2}
          title={t('compliance.retention.stats.lastPurge.title')}
          value={purgeHistory.length > 0 ? formatDate(purgeHistory[0]?.executed_at) : t('compliance.retention.stats.lastPurge.never')}
          description={purgeHistory.length > 0
            ? t('compliance.retention.stats.lastPurge.description', { count: purgeHistory[0]?.total_records_deleted || 0 })
            : t('compliance.retention.stats.lastPurge.noneDescription')}
          color="green"
        />
      </div>

      {/* Retention Guidelines by Country */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold mb-4 flex items-center">
          <Shield className="w-5 h-5 mr-2 text-blue-600" />
          {t('compliance.retention.countries.title')}
        </h4>
        <div className="space-y-3">
          <CountryGuideline
            country={`🇫🇷 ${t('compliance.countryRules.countries.FR')}`}
            guideline={t('compliance.retention.countries.FR')}
          />
          <CountryGuideline
            country={`🇩🇪 ${t('compliance.countryRules.countries.DE')}`}
            guideline={t('compliance.retention.countries.DE')}
          />
          <CountryGuideline
            country={`🇪🇸 ${t('compliance.countryRules.countries.ES')}`}
            guideline={t('compliance.retention.countries.ES')}
          />
          <CountryGuideline
            country={`🇮🇹 ${t('compliance.countryRules.countries.IT')}`}
            guideline={t('compliance.retention.countries.IT')}
          />
          <CountryGuideline
            country={`🇳🇱 ${t('compliance.countryRules.countries.NL')}`}
            guideline={t('compliance.retention.countries.NL')}
          />
        </div>
      </div>

      {/* Purge History */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold mb-4 flex items-center">
          <RefreshCw className="w-5 h-5 mr-2 text-gray-600" />
          {t('compliance.retention.history.title')}
        </h4>
        {purgeHistory.length === 0 ? (
          <p className="text-gray-500 text-center py-4">{t('compliance.retention.history.empty')}</p>
        ) : (
          <table className="w-full">
            <thead className="text-left text-sm text-gray-500">
              <tr>
                <th className="pb-2">{t('compliance.retention.history.table.date')}</th>
                <th className="pb-2">{t('compliance.retention.history.table.categories')}</th>
                <th className="pb-2">{t('compliance.retention.history.table.records')}</th>
                <th className="pb-2">{t('compliance.retention.history.table.status')}</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {purgeHistory.slice(0, 10).map((purge, idx) => {
                const categories = typeof purge.categories_purged === 'string'
                  ? JSON.parse(purge.categories_purged)
                  : purge.categories_purged || [];
                return (
                  <tr key={purge.id || idx} className="border-t">
                    <td className="py-2">{formatDate(purge.executed_at)}</td>
                    <td>{categories.map(c => categoryLabel(c)).join(', ') || t('compliance.retention.history.allCategories')}</td>
                    <td>{purge.total_records_deleted}</td>
                    <td>
                      <span className={purge.status === 'completed' ? 'text-green-600' : 'text-red-600'}>
                        {purge.status === 'completed' ? t('compliance.retention.history.status.completed') : purge.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-amber-600 mr-3 mt-0.5" />
          <div>
            <h4 className="font-semibold text-amber-800">{t('compliance.retention.warning.title')}</h4>
            <p className="text-sm text-amber-700 mt-1">
              {t('compliance.retention.warning.body')}
            </p>
          </div>
        </div>
      </div>

      {/* Purge Confirmation Modal */}
      {showPurgeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-red-600 flex items-center">
                <Trash2 className="w-5 h-5 mr-2" />
                {t('compliance.retention.confirm.title')}
              </h3>
              <button onClick={() => setShowPurgeConfirm(false)} disabled={purging}>
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800 font-medium">
                  {t('compliance.retention.confirm.warning', { count: totalToPurge })}
                </p>
                <p className="text-red-700 text-sm mt-2">
                  {t('compliance.retention.confirm.irreversible')}
                </p>
              </div>

              {stats.length > 0 && (
                <div className="mb-4">
                  <p className="font-medium text-gray-700 mb-2">{t('compliance.retention.confirm.details')}</p>
                  <ul className="space-y-1 text-sm">
                    {stats.filter(s => s.records_to_purge > 0).map(s => (
                      <li key={s.category} className="flex justify-between">
                        <span className="capitalize">{categoryLabel(s.category)}</span>
                        <span className="text-red-600">{t('compliance.retention.confirm.recordCount', { count: s.records_to_purge })}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowPurgeConfirm(false)}
                  disabled={purging}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  {t('common.actions.cancel')}
                </button>
                <button
                  onClick={handlePurge}
                  disabled={purging}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
                >
                  {purging ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('compliance.retention.confirm.purging')}
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('compliance.retention.confirm.title')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, title, value, description, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600'
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <h4 className="font-semibold text-gray-900">{value}</h4>
      <p className="text-sm text-gray-500">{title}</p>
      <p className="text-xs text-gray-400 mt-1">{description}</p>
    </div>
  );
}

function CountryGuideline({ country, guideline }) {
  return (
    <div className="flex items-start">
      <span className="text-lg mr-3">{country}</span>
      <span className="text-sm text-gray-600">{guideline}</span>
    </div>
  );
}

export default DataRetentionPanel;
